import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import './EmployeeDashboard.css';

const today = new Date();
const todayStr = today.toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long'});
const dateKey = today.toISOString().split('T')[0];
const LEAVE_TYPES = ['Casual Leave','Sick Leave','Earned Leave','Maternity Leave'];
const WORK_END_HOUR = 18; // 6 PM cutoff

function pushNotif(msg) {
  try {
    const n = JSON.parse(localStorage.getItem('ryzenos_notifs') || '[]');
    n.unshift({ id: Date.now(), msg, time: new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}), read: false });
    localStorage.setItem('ryzenos_notifs', JSON.stringify(n.slice(0, 50)));
  } catch {}
}

function calcHoursStatus(entries) {
  if (!entries || entries.length === 0) return { hours: '—', status: 'Pending' };
  let totalMins = 0;
  let pendingCheckout = false;
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].type === 'in') {
      const next = entries[i+1];
      if (next && next.type === 'out') {
        const [ih, im] = entries[i].time.split(':').map(Number);
        const [oh, om] = next.time.split(':').map(Number);
        totalMins += (oh*60+om) - (ih*60+im);
      } else {
        pendingCheckout = true;
      }
    }
  }
  const hrs = totalMins / 60;
  if (pendingCheckout) return { hours: hrs > 0 ? hrs.toFixed(1)+'h+' : '—', status: 'In Progress' };
  if (hrs >= 8) return { hours: hrs.toFixed(1)+'h', status: 'Full Day' };
  if (hrs >= 4) return { hours: hrs.toFixed(1)+'h', status: 'Half Day' };
  if (hrs > 0) return { hours: hrs.toFixed(1)+'h', status: 'Short' };
  return { hours: '—', status: 'Pending' };
}

const COLUMNS = [
  { id:'todo',       label:'To Do',       color:'#6B7280' },
  { id:'inprogress', label:'In Progress', color:'#3B82F6' },
  { id:'review',     label:'In Review',   color:'#F5A623' },
  { id:'done',       label:'Done',        color:'#22C55E' },
];

function EmpProjectsKanban({ user, pushNotif }) {
  const [filter, setFilter] = useState('mine'); // 'mine' | 'all'
  const [projects, setProjects] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ryzenos_projects') || '[]'); } catch { return []; }
  });
  const dragging = React.useRef(null);

  const myName = (user?.name || '').trim().toLowerCase();
  const visible = filter === 'mine'
    ? projects.filter(p => (p.assigned||'').trim().toLowerCase() === myName)
    : projects;

  const save = (next) => {
    setProjects(next);
    localStorage.setItem('ryzenos_projects', JSON.stringify(next));
  };

  const handleDrop = (colId) => {
    if (!dragging.current) return;
    const proj = projects.find(p => p.id === dragging.current);
    if (!proj) return;
    if ((proj.assigned||'').trim().toLowerCase() !== myName) { dragging.current = null; return; }
    const next = projects.map(p => p.id === proj.id ? { ...p, status: colId } : p);
    save(next);
    pushNotif(`${user.name} moved "${proj.title}" to ${COLUMNS.find(c=>c.id===colId)?.label}`);
    dragging.current = null;
  };

  return (
    <div className="emp2-content">
      <div className="emp2-filter-row">
        <button className={`emp2-filter-btn${filter==='mine'?' active':''}`} onClick={()=>setFilter('mine')}>My Tasks</button>
        <button className={`emp2-filter-btn${filter==='all'?' active':''}`} onClick={()=>setFilter('all')}>All Employees</button>
      </div>
      <div className="emp2-kanban">
        {COLUMNS.map(col => {
          const colTasks = visible.filter(p => p.status === col.id);
          return (
            <div key={col.id} className="emp2-kcol" onDragOver={e=>e.preventDefault()} onDrop={()=>handleDrop(col.id)}>
              <div className="emp2-kcol-hd">
                <span className="emp2-kcol-dot" style={{background:col.color}}/>
                <span>{col.label}</span>
                <span className="emp2-kcol-count">{colTasks.length}</span>
              </div>
              {colTasks.map(p => {
                const isMine = (p.assigned||'').trim().toLowerCase() === myName;
                return (
                  <div key={p.id} className="emp2-kcard" draggable={isMine} onDragStart={()=>{dragging.current = p.id;}} style={{opacity: isMine?1:0.7, cursor: isMine?'grab':'default'}}>
                    {p.priority && <span className={`emp2-prio ${p.priority.toLowerCase()}`}>{p.priority}</span>}
                    <div className="emp2-kcard-title">{p.title}</div>
                    <div className="emp2-kcard-assignee">{p.assigned}{isMine && <span style={{color:'#F5A623'}}> (you)</span>}</div>
                  </div>
                );
              })}
              {colTasks.length === 0 && <div className="emp2-empty" style={{fontSize:11}}>No tasks</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('dashboard');

  const attKey = `ryzenos_att_${user?.username}_${dateKey}`;
  const [att, setAtt] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(attKey) || 'null');
      if (saved) {
        // Migrate old single in/out shape to entries array
        if (!saved.entries && (saved.checkIn || saved.checkOut)) {
          const entries = [];
          if (saved.checkIn) entries.push({ type:'in', time: saved.checkIn, manual: saved.manualIn });
          if (saved.checkOut) entries.push({ type:'out', time: saved.checkOut, manual: saved.manualOut });
          return { entries };
        }
        return saved;
      }
    } catch {}
    return { entries: [] };
  });
  const saveAtt = (a) => { setAtt(a); localStorage.setItem(attKey, JSON.stringify(a)); };

  const entries = att.entries || [];
  const lastEntry = entries[entries.length - 1];
  const isCheckedIn = lastEntry && lastEntry.type === 'in';

  const [showManual, setShowManual] = useState(false);
  const [manualTime, setManualTime] = useState('');

  const isPastCutoff = () => new Date().getHours() >= WORK_END_HOUR;

  const handleCheckIn = () => {
    if (isPastCutoff()) { setShowManual(true); return; }
    const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const type = isCheckedIn ? 'out' : 'in';
    const newEntries = [...entries, { type, time }];
    saveAtt({ entries: newEntries });
    pushNotif(`${user.name} ${type==='in'?'checked in':'checked out'} at ${time}`);
  };

  const submitManual = () => {
    if (!manualTime) return;
    const type = isCheckedIn ? 'out' : 'in';
    const newEntries = [...entries, { type, time: manualTime, manual: true }];
    saveAtt({ entries: newEntries });
    pushNotif(`${user.name} added a manual ${type==='in'?'check-in':'check-out'} at ${manualTime} (after working hours)`);
    setShowManual(false);
    setManualTime('');
  };

  // Employee-created tasks
  const [newTaskText, setNewTaskText] = useState('');
  const addOwnTask = () => {
    if (!newTaskText.trim()) return;
    try {
      const team = JSON.parse(localStorage.getItem('ryzenos_team') || '[]');
      const updated = team.map(m => m.username === user?.username
        ? { ...m, taskList: [...(m.taskList||[]), { id: Date.now(), text: newTaskText, done: false, selfAdded: true }] }
        : m);
      localStorage.setItem('ryzenos_team', JSON.stringify(updated));
      pushNotif(`${user.name} added a new task: "${newTaskText}"`);
    } catch {}
    setNewTaskText('');
    setTab('tasksRefresh'); setTimeout(()=>setTab('tasks'), 0);
  };

  const requestDeleteTask = (taskId, taskText) => {
    try {
      const reqs = JSON.parse(localStorage.getItem('ryzenos_task_delete_requests') || '[]');
      reqs.push({ id: Date.now(), taskId, taskText, username: user.username, name: user.name, status: 'Pending' });
      localStorage.setItem('ryzenos_task_delete_requests', JSON.stringify(reqs));
      const n = JSON.parse(localStorage.getItem('ryzenos_notifs')||'[]');
      n.unshift({id:Date.now(), msg:`${user.name} requested to delete task "${taskText}" — needs approval`, time:new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}), read:false});
      localStorage.setItem('ryzenos_notifs', JSON.stringify(n.slice(0,50)));
      alert('Delete request sent to admin for approval.');
    } catch {}
  };

  // Tasks
  const tasks = (() => {
    try {
      const team = JSON.parse(localStorage.getItem('ryzenos_team') || '[]');
      const me = team.find(m => m.username === user?.username);
      const personalTasks = me?.taskList || [];

      const projects = JSON.parse(localStorage.getItem('ryzenos_projects') || '[]');
      const myName = (me?.name || user?.name || '').trim().toLowerCase();
      const myProjectTasks = projects
        .filter(p => (p.assigned || '').trim().toLowerCase() === myName)
        .map(p => ({
          id: 'proj-' + p.id,
          text: p.title,
          done: p.status === 'done',
          fromProject: true,
          status: p.status,
          priority: p.priority,
          due: p.due,
        }));

      return [...myProjectTasks, ...personalTasks];
    } catch { return []; }
  })();
  const doneCount = tasks.filter(t => t.done).length;

  // Leave
  const leaveKey = 'ryzenos_leave_requests';
  const [leaveForm, setLeaveForm] = useState({ type: 'Casual Leave', from: '', to: '', reason: '' });
  const [leaveMsg, setLeaveMsg] = useState('');
  const myLeaves = (() => {
    try { return JSON.parse(localStorage.getItem(leaveKey) || '[]').filter(l => l.username === user?.username); } catch { return []; }
  })();

  const applyLeave = () => {
    if (!leaveForm.from || !leaveForm.to || !leaveForm.reason.trim()) { setLeaveMsg('Please fill all fields'); return; }
    try {
      const all = JSON.parse(localStorage.getItem(leaveKey) || '[]');
      all.push({ id: Date.now(), username: user.username, name: user.name, ...leaveForm, status: 'Pending', applied: new Date().toLocaleDateString('en-IN') });
      localStorage.setItem(leaveKey, JSON.stringify(all));
      pushNotif(`${user.name} applied for ${leaveForm.type} (${leaveForm.from} to ${leaveForm.to})`);
      setLeaveMsg('Leave request submitted successfully');
      setLeaveForm({ type: 'Casual Leave', from: '', to: '', reason: '' });
      setTimeout(() => setLeaveMsg(''), 3000);
    } catch { setLeaveMsg('Error submitting leave'); }
  };

  // Settings
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ryzenos_user_profile') || 'null') || { name: user?.name || '', email: user?.email || '', phone: '' }; } catch { return { name: user?.name || '', email: user?.email || '', phone: '' }; }
  });
  const [pwForm, setPwForm] = useState({ newPw: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [profSaved, setProfSaved] = useState(false);

  const saveProfile = () => {
    localStorage.setItem('ryzenos_user_profile', JSON.stringify(profile));
    setProfSaved(true); setTimeout(() => setProfSaved(false), 2000);
  };
  const updatePassword = () => {
    if (pwForm.newPw.length < 6) { setPwMsg('Password must be at least 6 characters'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwMsg('Passwords do not match'); return; }
    try {
      const team = JSON.parse(localStorage.getItem('ryzenos_team') || '[]');
      localStorage.setItem('ryzenos_team', JSON.stringify(team.map(m => m.username === user.username ? { ...m, password: pwForm.newPw } : m)));
      setPwMsg('Password updated'); setPwForm({ newPw: '', confirm: '' });
      setTimeout(() => setPwMsg(''), 2000);
    } catch { setPwMsg('Error updating password'); }
  };

  const NAV = [
    { id: 'dashboard',  label: 'My Dashboard' },
    { id: 'tasks',      label: 'Projects' },
    { id: 'attendance', label: 'Attendance' },
    { id: 'leave',      label: 'Leave' },
    { id: 'settings',   label: 'Settings' },
  ];

  const hist = calcHoursStatus(entries);

  return (
    <div className="emp2-wrap" data-theme={theme}>
      <div className="emp2-sb">
        <div className="emp2-brand">
          <div className="emp2-brand-name">RyzenOS</div>
          <div className="emp2-brand-sub">Employee</div>
        </div>
        <div className="emp2-nav">
          {NAV.map(n => (
            <div key={n.id} className={`emp2-nav-item${tab===n.id?' active':''}`} onClick={()=>setTab(n.id)}>{n.label}</div>
          ))}
        </div>
        <div className="emp2-sb-bottom">
          <div className="emp2-user">
            <div className="emp2-user-av">{user?.initials || 'U'}</div>
            <div>
              <div className="emp2-user-name">{user?.name}</div>
              <div className="emp2-user-role">{user?.dept || 'Team'}</div>
            </div>
          </div>
          <div className="emp2-signout" onClick={logout}>Sign out</div>
        </div>
      </div>

      <div className="emp2-main">
        <div className="emp2-topbar">
          <div className="emp2-title">{NAV.find(n=>n.id===tab)?.label}</div>
          <div className="emp2-date">{today.toLocaleDateString('en-IN', {weekday:'short', day:'numeric', month:'short'})}</div>
        </div>

        {tab === 'dashboard' && (
          <div className="emp2-content">
            <div className="emp2-stat-row">
              <div className="emp2-stat"><div className="emp2-stat-lbl">Tasks assigned</div><div className="emp2-stat-val" style={{color:'#3B82F6'}}>{tasks.length}</div></div>
              <div className="emp2-stat"><div className="emp2-stat-lbl">Completed</div><div className="emp2-stat-val" style={{color:'#22C55E'}}>{doneCount}</div></div>
              <div className="emp2-stat"><div className="emp2-stat-lbl">Leave balance</div><div className="emp2-stat-val" style={{color:'#F5A623'}}>9 days</div></div>
            </div>
            <div className="emp2-card">
              <div className="emp2-card-hd">Today's tasks</div>
              {tasks.length===0 ? <div className="emp2-empty">No tasks assigned</div> : tasks.slice(0,5).map(t=>(
                <div key={t.id} className={`emp2-task${t.done?' done':''}`}>
                  <span className="emp2-task-dot" style={{background:t.done?'#22C55E':'#F5A623'}}/>
                  <span>{t.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'tasks' && (
          <EmpProjectsKanban user={user} pushNotif={pushNotif}/>
        )}

        {tab === 'attendance' && (
          <div className="emp2-content">
            <div className="emp2-card">
              <div className="emp2-card-hd">Today's attendance</div>
              <div className="emp2-att-box">
                <div className="emp2-att-date">{today.toLocaleDateString('en-IN', {weekday:'long', day:'numeric', month:'long'})}</div>
                {entries.length > 0 && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center',marginBottom:14}}>
                    {entries.map((e,i)=>(
                      <span key={i} className={`emp2-badge ${e.type==='in'?'amber':'green'}`}>
                        {e.type==='in'?'In':'Out'} {e.time}{e.manual?'*':''}
                      </span>
                    ))}
                  </div>
                )}
                {!showManual ? (
                  <button className="emp2-checkin-btn" onClick={handleCheckIn}>
                    {isCheckedIn ? 'Check Out' : 'Check In'}
                  </button>
                ) : (
                  <div className="emp2-manual-box">
                    <div className="emp2-manual-hint">Past 6 PM — enter time manually</div>
                    <input className="emp2-inp" type="time" value={manualTime} onChange={e=>setManualTime(e.target.value)}/>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button className="emp2-checkin-btn" style={{flex:1}} onClick={submitManual}>Submit</button>
                      <button className="emp2-cancel-btn" onClick={()=>setShowManual(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="emp2-card">
              <div className="emp2-card-hd-row">
                <div className="emp2-card-hd">Attendance history</div>
                <div className="emp2-hint">9AM–6PM = Full Day | 4h+ = Half Day</div>
              </div>
              <table className="emp2-table">
                <thead><tr>{['Date','Check In','Check Out','Hours','Status'].map(h=><th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  <tr>
                    <td>{dateKey}</td>
                    <td style={{color:'#F5A623'}}>{entries.find(e=>e.type==='in')?.time || '—'}</td>
                    <td style={{color: entries.filter(e=>e.type==='out').length ? '#22C55E' : '#ef4444'}}>{[...entries].reverse().find(e=>e.type==='out')?.time || 'Pending'}</td>
                    <td>{hist.hours}</td>
                    <td><span className={`emp2-badge ${hist.status==='Full Day'?'green':hist.status==='Half Day'?'amber':hist.status==='Pending'?'red':'gray'}`}>{hist.status}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'leave' && (
          <div className="emp2-content">
            <div className="emp2-two-col">
              <div className="emp2-card">
                <div className="emp2-card-hd">Apply for Leave</div>
                <div className="emp2-form-grp"><label>Leave Type</label>
                  <select className="emp2-inp" value={leaveForm.type} onChange={e=>setLeaveForm(f=>({...f,type:e.target.value}))}>
                    {LEAVE_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="emp2-form-row">
                  <div className="emp2-form-grp"><label>From</label><input className="emp2-inp" type="date" value={leaveForm.from} onChange={e=>setLeaveForm(f=>({...f,from:e.target.value}))}/></div>
                  <div className="emp2-form-grp"><label>To</label><input className="emp2-inp" type="date" value={leaveForm.to} onChange={e=>setLeaveForm(f=>({...f,to:e.target.value}))}/></div>
                </div>
                <div className="emp2-form-grp"><label>Reason</label><textarea className="emp2-inp" rows="3" value={leaveForm.reason} onChange={e=>setLeaveForm(f=>({...f,reason:e.target.value}))}/></div>
                {leaveMsg && <div className={`emp2-msg ${leaveMsg.includes('success')?'ok':'err'}`}>{leaveMsg}</div>}
                <button className="emp2-checkin-btn" style={{width:'100%'}} onClick={applyLeave}>Submit Request</button>
              </div>
              <div className="emp2-card">
                <div className="emp2-card-hd">My Leave Requests</div>
                {myLeaves.length===0 ? <div className="emp2-empty">No leave requests yet</div> : myLeaves.map(l=>(
                  <div key={l.id} className="emp2-leave-item">
                    <div style={{fontWeight:600,fontSize:13}}>{l.type}</div>
                    <div style={{fontSize:12,opacity:.6}}>{l.from} → {l.to}</div>
                    <div style={{fontSize:12,opacity:.6}}>{l.reason}</div>
                    <span className={`emp2-badge ${l.status==='Approved'?'green':l.status==='Declined'?'red':'amber'}`}>{l.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <div className="emp2-content">
            <div className="emp2-two-col">
              <div className="emp2-card">
                <div className="emp2-card-hd">My Profile</div>
                <div className="emp2-form-grp"><label>Name</label><input className="emp2-inp" value={profile.name} onChange={e=>setProfile(p=>({...p,name:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Email</label><input className="emp2-inp" value={profile.email} onChange={e=>setProfile(p=>({...p,email:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Phone</label><input className="emp2-inp" placeholder="+91 XXXXX XXXXX" value={profile.phone} onChange={e=>setProfile(p=>({...p,phone:e.target.value}))}/></div>
                <button className="emp2-checkin-btn" onClick={saveProfile}>{profSaved?'✓ Saved':'Save changes'}</button>
              </div>
              <div className="emp2-card">
                <div className="emp2-card-hd">Change Password</div>
                <div className="emp2-form-grp"><label>New password</label><input className="emp2-inp" type="password" value={pwForm.newPw} onChange={e=>setPwForm(f=>({...f,newPw:e.target.value}))}/></div>
                <div className="emp2-form-grp"><label>Confirm password</label><input className="emp2-inp" type="password" value={pwForm.confirm} onChange={e=>setPwForm(f=>({...f,confirm:e.target.value}))}/></div>
                {pwMsg && <div className={`emp2-msg ${pwMsg.includes('updated')?'ok':'err'}`}>{pwMsg}</div>}
                <button className="emp2-checkin-btn" onClick={updatePassword}>Update Password</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
